// The chrome's icons come from lucide: one drawing language, already drawn properly,
// already a dependency here. Wrapped in this file rather than imported at each call site,
// so the set stays a decision made in one place.
//
// Two exceptions live elsewhere: the brand marks in brand-icons.tsx, which belong to
// their owners, and tau below, which is a letter rather than an icon.
import { ArrowDown, ArrowUp, ArrowUpDown, BookOpen, Check, ChevronDown, Copy, Crown, ExternalLink, Moon, Pin, Sun } from "lucide-react";

const SIZE = 17;
const STROKE = 1.5;

interface IconProps {
  className?: string;
}

export function BookIcon({ className }: IconProps) {
  return <BookOpen size={SIZE} strokeWidth={STROKE} className={className} aria-hidden />;
}

/** Marks the best score in a column. Small enough to sit inside a table cell. */
export function CrownIcon({ className }: IconProps) {
  return <Crown size={12} strokeWidth={2} className={className} aria-hidden />;
}

/**
 * The sort state of a column, as one mark that occupies the same space in all three
 * states: a column that gains or loses an arrow when clicked would resize itself.
 */
export function SortIcon({ direction, className }: IconProps & { direction: "asc" | "desc" | null }) {
  const Mark = direction === "asc" ? ArrowUp : direction === "desc" ? ArrowDown : ArrowUpDown;
  return <Mark size={10} strokeWidth={2} className={className} aria-hidden />;
}

export function CopyIcon({ className }: IconProps) {
  return <Copy size={14} strokeWidth={1.75} className={className} aria-hidden />;
}

/** "This one is mine" - filled once set, so the state reads without a label. */
export function PinIcon({ className, filled = false }: IconProps & { filled?: boolean }) {
  return <Pin size={14} strokeWidth={1.75} fill={filled ? "currentColor" : "none"} className={className} aria-hidden />;
}

export function CheckIcon({ className }: IconProps) {
  return <Check size={14} strokeWidth={2} className={className} aria-hidden />;
}

export function ChevronIcon({ className }: IconProps) {
  return <ChevronDown size={SIZE} strokeWidth={STROKE} className={className} aria-hidden />;
}

export function ExternalIcon({ className }: IconProps) {
  return <ExternalLink size={13} strokeWidth={STROKE} className={className} aria-hidden />;
}

/**
 * A half-filled circle was tried here and could not be read at this size - at 17px the
 * fill and the outline merge. A sun and a moon survive the scale, so the control shows
 * the theme it will switch to.
 */
export function SunIcon({ className }: IconProps) {
  return <Sun size={16} strokeWidth={STROKE} className={className} aria-hidden />;
}

export function MoonIcon({ className }: IconProps) {
  return <Moon size={16} strokeWidth={STROKE} className={className} aria-hidden />;
}
